/**
 * Utility functions for array manipulation used across the application
 */

/**
 * Performs a Fisher-Yates (Knuth) shuffle on an array
 * @param array The array to shuffle
 * @returns A new shuffled array (original array is not modified)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array]; // Create a copy to avoid mutating the original
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]]; // Swap elements
  }
  return result;
}

/**
 * Creates a controlled interleaving of multiple arrays
 * Ensures that items of the same type are not clustered together
 * @param arrays Arrays to interleave
 * @returns A new array with elements interleaved
 */
export function interleaveArrays<T>(...arrays: T[][]): T[] {
  if (arrays.length === 0) return [];
  if (arrays.length === 1) return [...arrays[0]];
  
  // Create a copy of each array to avoid mutating originals
  const arrayCopies = arrays.map(arr => [...arr]);
  
  // Calculate total length
  const totalLength = arrayCopies.reduce((sum, arr) => sum + arr.length, 0);
  const result: T[] = [];
  
  // Keep track of the last array index used
  let lastUsedIndex = -1;
  
  while (result.length < totalLength) {
    // Find the next non-empty array (avoiding consecutive use of the same array if possible)
    let availableArrays = arrayCopies.map((arr, i) => arr.length > 0 ? i : -1).filter(i => i !== -1);
    
    // If we have multiple available arrays, avoid using the last one if possible
    if (availableArrays.length > 1 && availableArrays.includes(lastUsedIndex)) {
      availableArrays = availableArrays.filter(i => i !== lastUsedIndex);
    }
    
    // If we have arrays to choose from, pick one (randomly if multiple)
    if (availableArrays.length > 0) {
      const selectedIndex = availableArrays[Math.floor(Math.random() * availableArrays.length)];
      result.push(arrayCopies[selectedIndex].shift()!);
      lastUsedIndex = selectedIndex;
    } else {
      // No non-empty arrays left, break the loop
      break;
    }
  }
  
  return result;
} 